import React from 'react';
import { Download, Upload } from 'lucide-react';
import { Feed, FeedStatus, FeedLastRefresh, SortOrder } from '../../types';
import { Language, Theme, Translations } from '../../i18n/translations';
import { FeedManager } from './FeedManager';
import { SettingsStats, FeedStats } from './SettingsStats';
import { TimeLimitSettings } from './TimeLimitSettings';
import { ArticlesPerPageSettings } from './ArticlesPerPageSettings';
import { LanguageSettings } from './LanguageSettings';
import { ThemeSettings } from './ThemeSettings';
import { ArticleParsingSettings } from './ArticleParsingSettings';
import { SortOrderSettings } from './SortOrderSettings';
import { ControlBarLegend } from '../ControlBarLegend';

interface SettingsPanelProps {
  feeds: Feed[];
  feedStatus: FeedStatus;
  feedLastRefresh: FeedLastRefresh;
  newFeedUrl: string;
  setNewFeedUrl: (url: string) => void;
  newFeedName: string;
  setNewFeedName: (name: string) => void;
  onAddFeed: () => void;
  onAddFeeds: (feeds: Feed[]) => void;
  onRemoveFeed: (url: string) => void;
  onReloadFeed: (feed: Feed) => void;
  error: string;
  dailyTimeLimit: number;
  onTimeLimitChange: (minutes: number) => void;
  articlesPerPage: number;
  onArticlesPerPageChange: (count: number) => void;
  language: Language;
  onLanguageChange: (lang: Language) => void;
  theme: Theme;
  onThemeChange: (theme: Theme) => void;
  sortOrder: SortOrder;
  onSortOrderChange: (order: SortOrder) => void;
  articleParsingEnabled: boolean;
  onArticleParsingChange: (enabled: boolean) => void;
  t: Translations;
  onExport: () => void;
  onImport: (event: React.ChangeEvent<HTMLInputElement>) => void;
  feedStats: FeedStats;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  feeds,
  feedStatus,
  feedLastRefresh,
  newFeedUrl,
  setNewFeedUrl,
  newFeedName,
  setNewFeedName,
  onAddFeed,
  onAddFeeds,
  onRemoveFeed,
  onReloadFeed,
  error,
  dailyTimeLimit,
  onTimeLimitChange,
  articlesPerPage,
  onArticlesPerPageChange,
  language,
  onLanguageChange,
  theme,
  onThemeChange,
  sortOrder,
  onSortOrderChange,
  articleParsingEnabled,
  onArticleParsingChange,
  t,
  onExport,
  onImport,
  feedStats,
}) => {
  return (
    <section className="bg-gray-50 dark:bg-gray-900 border-b-4 border-gray-300 dark:border-gray-600" aria-labelledby="settings-heading">
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <h2 id="settings-heading" className="text-xl sm:text-2xl font-bold font-serif mb-4 text-gray-900 dark:text-white">{t.manageFeeds}</h2>

        <SettingsStats stats={feedStats} t={t} />

        <FeedManager
          feeds={feeds}
          feedStatus={feedStatus}
          feedLastRefresh={feedLastRefresh}
          newFeedUrl={newFeedUrl}
          setNewFeedUrl={setNewFeedUrl}
          newFeedName={newFeedName}
          setNewFeedName={setNewFeedName}
          onAddFeed={onAddFeed}
          onAddFeeds={onAddFeeds}
          onRemoveFeed={onRemoveFeed}
          onReloadFeed={onReloadFeed}
          error={error}
          t={t}
          language={language}
        />

        <ArticleParsingSettings
          articleParsingEnabled={articleParsingEnabled}
          onArticleParsingChange={onArticleParsingChange}
          t={t}
        />

        <SortOrderSettings
          sortOrder={sortOrder}
          onSortOrderChange={onSortOrderChange}
          t={t}
        />

        {/* Control Bar Legend */}
        <div className="mb-4">
          <ControlBarLegend t={t} />
        </div>

        <TimeLimitSettings
          dailyTimeLimit={dailyTimeLimit}
          onTimeLimitChange={onTimeLimitChange}
          t={t}
        />

        <ArticlesPerPageSettings
          articlesPerPage={articlesPerPage}
          onArticlesPerPageChange={onArticlesPerPageChange}
          t={t}
        />

        <LanguageSettings
          language={language}
          onLanguageChange={onLanguageChange}
          t={t}
        />

        <ThemeSettings
          theme={theme}
          onThemeChange={onThemeChange}
          t={t}
        />

        <div className="flex gap-2">
          <button
            onClick={onExport}
            disabled={feeds.length === 0}
            className="px-4 py-2 bg-green-700 text-white font-semibold hover:bg-green-800 disabled:bg-gray-300"
            aria-label={t.exportBtn}
          >
            <Download className="w-4 h-4 inline mr-1" aria-hidden="true" />
            {t.exportBtn}
          </button>
          <label className="px-4 py-2 bg-purple-700 text-white font-semibold cursor-pointer hover:bg-purple-800">
            <Upload className="w-4 h-4 inline mr-1" aria-hidden="true" />
            {t.importBtn}
            <input
              type="file"
              accept=".json"
              onChange={onImport}
              className="hidden"
              aria-label={t.importBtn}
            />
          </label>
        </div>
      </div>
    </section>
  );
};
